import React, { useState } from 'react';
import { Brain, HardDrive, Cpu, Clock } from 'lucide-react';

interface AIMLToolsProps {
  category: string;
}

export const AIMLTools: React.FC<AIMLToolsProps> = ({ category }) => {
  const [modelParams, setModelParams] = useState('');
  const [batchSize, setBatchSize] = useState('1');
  const [precision, setPrecision] = useState('fp32');
  const [results, setResults] = useState<{
    vramGB: number;
    throughput: number;
    modelSizeGB: number;
  } | null>(null);

  const calculateRequirements = (e: React.FormEvent) => {
    e.preventDefault();
    
    const params = parseFloat(modelParams);
    const batch = parseInt(batchSize);
    
    if (isNaN(params) || isNaN(batch)) return;

    // Calculate model size and VRAM requirements
    const bytesPerParam = precision === 'fp32' ? 4 : 
                         precision === 'fp16' ? 2 : 1; // int8
    
    const modelSizeGB = (params * bytesPerParam) / (1024 * 1024 * 1024);
    const vramGB = modelSizeGB * batch * 1.2; // 20% overhead for activations
    const throughput = 1000 / (params * 0.00001 * batch); // Estimated tokens/sec

    setResults({
      vramGB,
      throughput,
      modelSizeGB
    });
  };

  return (
    <div className="mt-8 p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">AI Model Calculator</h2>
          <p className="text-gray-600 text-sm mt-1">
            Calculate VRAM requirements and estimated throughput for AI models
          </p>
        </div>
      </div>

      <form onSubmit={calculateRequirements} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model Parameters
            </label>
            <input
              type="number"
              value={modelParams}
              onChange={(e) => setModelParams(e.target.value)}
              placeholder="e.g., 7000000000"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Size
            </label>
            <input
              type="number"
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              min="1"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precision
            </label>
            <select
              value={precision}
              onChange={(e) => setPrecision(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="fp32">FP32</option>
              <option value="fp16">FP16</option>
              <option value="int8">INT8</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Calculate Requirements
        </button>
      </form>

      {results && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="w-4 h-4 text-blue-600" />
              <h3 className="font-medium text-gray-900">Model Size</h3>
            </div>
            <p className="text-2xl font-semibold text-blue-600">
              {results.modelSizeGB.toFixed(2)} GB
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="w-4 h-4 text-blue-600" />
              <h3 className="font-medium text-gray-900">VRAM Required</h3>
            </div>
            <p className="text-2xl font-semibold text-blue-600">
              {results.vramGB.toFixed(2)} GB
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <h3 className="font-medium text-gray-900">Est. Throughput</h3>
            </div>
            <p className="text-2xl font-semibold text-blue-600">
              {results.throughput.toFixed(1)} tokens/s
            </p>
          </div>
        </div>
      )}
    </div>
  );
}; 